/**
 * Ob ein Klick auf eine klickbare Zeile (Tabellenzeile, Karte) als
 * Zeilen-Navigation gelten darf. Eine Stelle für ALLE klickbaren Zeilen —
 * `DataTable` und die handgebauten Tabellen über `TR` nutzen dieselbe Prüfung,
 * statt `stopPropagation` an jeder Zelle zu verteilen (CLAUDE.md §9).
 *
 * Vier Fälle sind KEIN Zeilenklick:
 *
 * 1. **Klicks aus einem Portal** (Dialog, Dropdown, Tooltip). React leitet sie
 *    durch den **Komponenten**-Baum weiter, nicht durch den DOM: ein Modal, das
 *    aus der Zeile geöffnet wird, hängt an `body` — sein Klick landete trotzdem
 *    im Zeilen-Handler, und das Schließen der Vorschau öffnete den Datensatz
 *    (Vorfall 2026-07-30 in der Belegliste). `contains` unterscheidet das.
 *
 * 2. **Klicks auf einen echten Link IN der Zeile.** Sonst navigiert der Anker
 *    UND die Zeile — und die Zeile gewinnt, weil ihr `navigate()` später läuft.
 *    Genau das war der Fehler in der Einkaufsliste: Ein Klick auf die
 *    Projekt-Spalte (echter `<Link>` über `ProjectRef`) landete im
 *    Bestell-Editor statt im Projekt. Die Verkaufsliste hatte dafür ein
 *    einzelnes `stopPropagation`-Pflaster, die Einkaufsliste nicht.
 *
 * 3. **Modifier-Klicks** (Cmd/Strg/Umschalt/Alt). Die gehören dem Browser:
 *    neuer Tab, neues Fenster, Mehrfachauswahl. Eine SPA-Navigation würde sie
 *    verschlucken.
 *
 * 4. **Nicht-linke Maustaste** und bereits behandelte Ereignisse
 *    (`defaultPrevented`).
 */
export function shouldHandleRowClick(event: {
  currentTarget: EventTarget | null;
  target: EventTarget | null;
  defaultPrevented: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  button: number;
}): boolean {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;

  const container = event.currentTarget as Node | null;
  const target = event.target as Node | null;
  if (!container || !target) return false;
  if (!container.contains(target)) return false;

  // `closest` gibt es nur auf Elementen — bei einem Klick auf einen Textknoten
  // (kommt in Safari vor) vom Elternelement aus suchen.
  const element =
    target.nodeType === 1 ? (target as Element) : (target.parentElement as Element | null);
  if (element?.closest('a[href]')) return false;

  return true;
}
